import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/* The published content layer.
 
   One Firestore document, site/content, holds every media override the admin
   console has saved: { videos: { key: {...} }, images: { key: {...} } }.
   Pages ask for a slot by key and get the override when one exists, or the
   value hard-coded in the page's data file when it does not.
 
   Two properties matter here. First, the site works perfectly with no
   Firestore document at all, which is what keeps the prerendered HTML and the
   local development experience intact. Second, nothing blocks on the network:
   the fallback renders immediately and an override swaps in when it arrives. */

const ContentContext = createContext({ videos: {}, images: {}, loaded: false });

const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

export function ContentProvider({ children }) {
  const [content, setContent] = useState({ videos: {}, images: {}, loaded: false });

  useEffect(() => {
    if (isPrerender) return undefined;
    let active = true;
    const unsubscribe = onSnapshot(
      doc(db, 'site', 'content'),
      (snap) => {
        if (!active) return;
        const data = snap.exists() ? snap.data() : {};
        setContent({ videos: data.videos || {}, images: data.images || {}, loaded: true });
      },
      () => {
        // Offline, rules-blocked, or misconfigured: the site keeps its defaults.
        if (active) setContent((c) => ({ ...c, loaded: true }));
      }
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

/* Returns a video object shaped the way VideoSlot expects, or the fallback
   when no override is published. An override can carry either an embed URL
   or an uploaded file URL, plus an optional poster and caption. */
export function useVideo(key, fallback = null) {
  const { videos } = useContent();
  return useMemo(() => {
    const v = videos[key];
    if (!v || (!v.embed && !v.src)) return fallback;
    return {
      embed: v.embed || undefined,
      src: v.src || undefined,
      poster: v.poster || undefined,
      caption: v.caption || undefined,
    };
  }, [videos, key, fallback]);
}

/* Returns an image URL string: the override when published, the fallback
   otherwise. Empty string means deliberately blank. */
export function useImage(key, fallback = '') {
  const { images } = useContent();
  const img = images[key];
  return img && img.src ? img.src : fallback;
}

/* Convenience for a gallery: takes the page's static screenshot array and a
   parallel list of slot keys, and returns the array with any overrides
   applied in place. */
export function useImageList(entries) {
  const { images } = useContent();
  return useMemo(
    () =>
      entries.map((entry) => {
        const override = images[entry.key];
        if (!override || !override.src) return entry;
        return { ...entry, src: override.src, caption: override.caption || entry.caption };
      }),
    [entries, images]
  );
}
